/**
 * ViewportContextMenu — the in-Canvas half of the viewport right-click menu
 * (ENG-1050): it owns the gesture and the ground raycast, and hands the result
 * to viewportMenuStore. The markup half is ViewportMenu.jsx, mounted in DOM
 * land; see the store's header for why the two cannot be one component.
 *
 * This component renders NOTHING (returns null) — every JSX element here would
 * be reconciled by R3F as a THREE object.
 *
 * Three gesture facts this relies on, each verified rather than assumed:
 *
 *   - inputComponent._onContextMenu suppresses the native menu only for a right
 *     DRAG (>3px), never for a stationary right-click, so `contextmenu` is
 *     exactly the "clicked, didn't orbit" signal. It preventDefaults without
 *     stopping propagation, so this listener still sees the event.
 *   - three's OrbitControls likewise only preventDefaults. Same conclusion.
 *   - While the wizard's waypoint picker is ARMED it owns the canvas gesture
 *     (and on macOS Ctrl+left-click IS a secondary click), so this menu stands
 *     down entirely rather than popping over a pick.
 */
import { useEffect } from 'react'
import { useThree } from '@react-three/fiber'
import * as THREE from 'three'
import { viewportCommandsFor } from '../lib/viewportCommands'
import { avatarFloor } from '../lib/avatarFloor'
import { viewportMenuStore } from '../lib/viewportMenuStore'
import { waypointPickerStore } from '../lib/waypointPickerStore'
import '../lib/walkHereCommand' // side-effect: registers the 'walkHere' command
import { resolveAvatarTransformTarget } from '../lib/avatarTransformTarget'

// Module-scope scratch — no per-event allocation (the CLAUDE.md rule for the
// picker's raycast applies to the same recipe here).
const _raycaster = new THREE.Raycaster()
const _plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0)
const _hit = new THREE.Vector3()
const _ndc = new THREE.Vector2()

export default function ViewportContextMenu() {
  const gl = useThree(s => s.gl)
  const camera = useThree(s => s.camera)

  useEffect(() => {
    const el = gl.domElement

    const onContextMenu = (e) => {
      // The picker owns the canvas while armed (see the header).
      if (waypointPickerStore.getState().armed) return
      e.preventDefault()

      const rect = el.getBoundingClientRect()
      const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1
      const ny = -((e.clientY - rect.top) / rect.height) * 2 + 1
      const nm = window.__NAVMESH_INSTANCE__ || null
      // The ground plane is the floor the AVATAR stands on, never the navmesh's
      // own `floorY`: a baked navmesh carries the height of the floor in the
      // BAKE's space (Startup_Level's is -6.2 while the world floor is 0), so
      // raycasting against that put the plane metres under the visible floor and
      // the hit landed metres away — which then surfaced as the nonsense
      // "clicked 7.33 m off the navmesh" on a navmesh that was correctly placed.
      // avatarFloor is the documented owner of that Y.
      _plane.constant = -avatarFloor.getY()
      _raycaster.setFromCamera(_ndc.set(nx, ny), camera)
      // A miss (camera under the floor plane, looking away) is a REASON carried
      // into the commands, not a silently swallowed right-click: the menu still
      // opens and says why nothing is available.
      const hit = _raycaster.ray.intersectPlane(_plane, _hit) ? [_hit.x, _hit.y, _hit.z] : null

      const ctx = { point: hit, navmesh: nm, hasAvatar: !!resolveAvatarTransformTarget() }
      viewportMenuStore.openAt({ x: e.clientX, y: e.clientY, items: viewportCommandsFor(ctx), ctx })
    }

    el.addEventListener('contextmenu', onContextMenu)
    return () => {
      el.removeEventListener('contextmenu', onContextMenu)
      viewportMenuStore.close() // never leave a menu pointing at a dead camera
    }
  }, [gl, camera])

  return null
}
